import React, { useEffect, useState } from "react";
import {
  Plus,
  Search,
  MessageSquare,
  Image as ImageIcon,
  FileText,
  Layout,
  List,
  CheckCircle,
  Clock,
  Edit,
  Trash2,
  Eye,
  Loader,
  AlertCircle,
  RefreshCw,
  XCircle,
  Pause,
  Copy,
  CloudDownload,
  GitCompare,
  Ban,
  Archive,
  AlertTriangle,
  Lock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getTemplates,
  deleteTemplate,
  updateTemplate,
  refreshTemplateStatus,
  getTemplateById,
  syncTemplatesFromMeta,
  getTemplateFilterOptions,
} from "../apis/template";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";
import ViewEditTemplateModal from "../components/ViewEditTemplateModal";
import CompareTemplatesModal from "../components/CompareTemplatesModal";
import PhoneSelector from "../components/PhoneSelector";
import TemplateCheckboxFilter from "../components/TemplateCheckboxFilter";
import TemplateTimeRangeFilter from "../components/TemplateTimeRangeFilter";
import { getLanguageLabel } from "../constants/templateLanguages";
import {
  TEMPLATE_STATUS_FILTER_OPTIONS,
  TEMPLATE_TIME_RANGE_OPTIONS,
  getCategoryLabel,
} from "../constants/templateFilterOptions";

const Templates = () => {
  const { selectedPhoneId, selectedPhoneAccount } = usePhoneAccount();
  const [userData, setUserData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState([]);
  const [daysBack, setDaysBack] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    languages: [],
    categories: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [error, setError] = useState("");
  const [refreshingId, setRefreshingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalTemplates, setTotalTemplates] = useState(0);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showCompare, setShowCompare] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      const parsed = JSON.parse(user);
      setUserData(parsed);
    }
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedCategories,
    selectedLanguages,
    selectedStatusFilters,
    daysBack,
    searchTerm,
  ]);

  // Load filter options (used languages/categories only) when phone changes
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const response = await getTemplateFilterOptions({
          phoneId: selectedPhoneId || null,
        });
        if (response.data?.success) {
          setFilterOptions({
            languages: response.data.languages || [],
            categories: response.data.categories || [],
          });
        }
      } catch (err) {
        console.error("Error loading filter options:", err);
      }
    };
    loadFilterOptions();
  }, [selectedPhoneId]);

  // Fetch templates when page, items per page, filters, or phoneId change
  useEffect(() => {
    fetchTemplates(currentPage, itemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    itemsPerPage,
    selectedCategories,
    selectedLanguages,
    selectedStatusFilters,
    daysBack,
    searchTerm,
    selectedPhoneId,
  ]);

  // Helper function to extract display data from template for table view
  const getTemplateDisplayData = (template) => {
    const templateJson = template.template_json || {};
    const components = templateJson.components || [];

    // Extract basic info for display
    const bodyComponent = components.find(c => c.type === 'BODY');
    const headerComponent = components.find(c => c.type === 'HEADER');
    const buttonsComponent = components.find(c => c.type === 'BUTTONS');

    const body = bodyComponent?.text || '';
    const headerType = headerComponent?.format?.toLowerCase() || 'none';
    const rawButtons = buttonsComponent?.buttons || [];

    // Determine template type for icon display
    let templateType = 'text';
    if (rawButtons.length > 0) {
      templateType = 'buttons';
    } else if (headerType !== 'none' && headerType !== 'text') {
      templateType = headerType;
    }

    return {
      ...template,
      body,
      template_type: templateType,
      header_type: headerType,
      meta_template_id: template.template_id || template.meta_template_id,
    };
  };

  const fetchTemplates = async (page = currentPage, perPage = itemsPerPage) => {
    setIsLoading(true);
    setError("");
    try {
      // Calculate offset based on page number
      const offset = (page - 1) * perPage;

      // Prepare filters for backend
      const filters = {
        phoneId: selectedPhoneId || null,
        limit: perPage,
        offset,
      };

      if (selectedCategories.length) {
        filters.categories = selectedCategories;
      }
      if (selectedLanguages.length) {
        filters.languages = selectedLanguages;
      }
      if (selectedStatusFilters.length) {
        filters.statusFilters = selectedStatusFilters;
      }
      if (daysBack) {
        filters.daysBack = daysBack;
      }
      if (searchTerm.trim()) {
        filters.searchTerm = searchTerm.trim();
      }

      // API interceptor handles token automatically
      const response = await getTemplates(filters);

      // Get templates and add display data only
      const rawTemplates = response.data.metaTemplates || [];
      const templatesWithDisplay = rawTemplates.map(getTemplateDisplayData);

      // Get total count from backend
      const total = response.data.total || 0;
      setTotalTemplates(total);

      // Get counts from backend response (array format: [{status: 'APPROVED', count: 10}, ...])
      const backendCounts = response.data.counts || [];
      const countsObj = {
        approved: 0,
        pending: 0,
        rejected: 0
      };

      // Transform backend counts array to object format
      backendCounts.forEach((item) => {
        const status = item.status?.toLowerCase();
        if (status && countsObj.hasOwnProperty(status)) {
          countsObj[status] = item.count || 0;
        }
      });

      setTemplates(templatesWithDisplay);
      setCounts(countsObj);
    } catch (err) {
      console.error("Error fetching templates:", err);
      setError(err.response?.data?.error || "Failed to load templates");
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh template status from Meta
  const handleRefreshStatus = async (template) => {
    if (!template.template_id) {
      alert("This template hasn't been submitted to Meta yet.");
      return;
    }

    setRefreshingId(template.id);
    setError("");

    try {
      // API interceptor handles token automatically
      const response = await refreshTemplateStatus(template.id);

      if (response.data.success) {
        // Update local state
        setTemplates((prev) =>
          prev.map((t) =>
            t.id === template.id
              ? {
                ...t,
                status: response.data.status,
                rejected_reason: response.data.rejectedReason || null,
              }
              : t
          )
        );

        // Show success message
        const statusMessage =
          response.data.status?.toLowerCase() === "approved"
            ? "✅ Template approved by Meta!"
            : response.data.status === "rejected"
              ? "❌ Template rejected by Meta"
              : `Status updated to: ${response.data.status}`;

        alert(statusMessage);

        // Refresh counts
        await fetchTemplates();
      }
    } catch (err) {
      console.error("Error refreshing status:", err);
      const errorMessage =
        err.response?.data?.error || "Failed to refresh template status";
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleViewEdit = async (template) => {
    // Fetch full template details for view/edit modal
    try {
      // API interceptor handles token automatically
      const response = await getTemplateById(template.id);
      const fullTemplate = response.data.template || template;
      setSelectedTemplate(fullTemplate);
      setShowModal(true);
    } catch (err) {
      console.error("Error fetching template:", err);
      setError(err.response?.data?.error || "Failed to load template details");
    }
  };

  const handleSaveTemplate = async (templateId, updateData) => {
    try {
      // API interceptor handles token automatically
      await updateTemplate(templateId, updateData);

      // Refresh templates list
      await fetchTemplates();

      return true;
    } catch (err) {
      console.error("Error saving template:", err);
      throw err;
    }
  };

  const handleDelete = async (templateId) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        // API interceptor handles token automatically
        await deleteTemplate(templateId);
        setTemplates((prev) => prev.filter((t) => t.id !== templateId));
        await fetchTemplates(); // Refresh counts
      } catch (err) {
        console.error("Error deleting template:", err);
        setError(err.response?.data?.error || "Failed to delete template");
      }
    }
  };

  // Duplicate template handler
  const handleDuplicate = async (template) => {
    setDuplicatingId(template.id);
    setError("");

    try {
      // API interceptor handles token automatically
      const response = await getTemplateById(template.id);
      const fullTemplate = response.data.template || template;

      // Prepare duplicate data - transformDuplicateTemplate will handle the conversion
      const duplicateData = {
        name: `copy_${fullTemplate.name}`,
        category: fullTemplate.category,
        language: fullTemplate.language,
        originalName: fullTemplate.name,
        header_media_url: fullTemplate.header_media_url || null,
        template_json: fullTemplate.template_json,
        waba_id: fullTemplate.waba_id,
        phone_id: fullTemplate.phone_id,
        parameter_format: fullTemplate.parameter_format || fullTemplate.template_json?.parameter_format || "numbered",
      };

      // Navigate to CreateTemplate - transformDuplicateTemplate will handle the rest
      navigate("/templates/create", {
        state: {
          duplicateTemplate: duplicateData,
        },
      });
    } catch (err) {
      console.error("Error duplicating template:", err);
      const errorMessage =
        err.response?.data?.error || "Failed to duplicate template";
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setDuplicatingId(null);
    }
  };

  // Sync templates from Meta
  const handleSyncTemplates = async () => {
    // Use selected account from context (updated when user changes account in header)
    const wabaAccount = selectedPhoneAccount;
    const wabaId = wabaAccount?.wabaId;
    const phoneId = wabaAccount?.phoneId;

    if (!wabaId || !phoneId) {
      setError("Please select a WhatsApp Business Account first");
      return;
    }

    setIsSyncing(true);
    setError("");
    setSyncSuccess(null);

    try {
      // API interceptor handles token automatically
      const response = await syncTemplatesFromMeta(wabaId, phoneId);

      if (response.data.success) {
        const stats = response.data.stats || {};
        const fetched = stats.fetched ?? stats.total ?? 0;
        const inserted = stats.inserted ?? 0;
        const updated = stats.updated ?? 0;
        const deleted = stats.deleted ?? 0;

        console.debug("[TemplateSync] client stats:", stats);

        setSyncSuccess({
          message: `Synced ${fetched} template${fetched === 1 ? "" : "s"} from Meta`,
          details: `${inserted} new, ${updated} updated, ${deleted} removed`,
          duration: stats.duration ?? 0,
        });

        // Refresh templates list and filter options
        await fetchTemplates();
        try {
          const optsRes = await getTemplateFilterOptions({
            phoneId: selectedPhoneId || null,
          });
          if (optsRes.data?.success) {
            setFilterOptions({
              languages: optsRes.data.languages || [],
              categories: optsRes.data.categories || [],
            });
          }
        } catch (_) {}

        // Auto-hide success message after 5 seconds
        setTimeout(() => setSyncSuccess(null), 5000);
      }
    } catch (err) {
      console.error("Error syncing templates:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.details ||
        "Failed to sync templates from Meta";
      setError(errorMessage);
    } finally {
      setIsSyncing(false);
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      text: MessageSquare,
      image: ImageIcon,
      document: FileText,
      buttons: Layout,
      list: List,
    };
    return icons[type] || MessageSquare;
  };

  const getStatusBadge = (status) => {
    if (!status) return {
      icon: Edit,
      label: "DRAFT",
      class: "bg-gray-100 text-gray-700 border-gray-200",
    };

    // Normalize status: convert to string, lowercase, and trim whitespace
    const normalizedStatus = String(status).toLowerCase().trim();

    const configs = {
      approved: {
        icon: CheckCircle,
        label: "APPROVED",
        class: "bg-emerald-100 text-emerald-700 border-emerald-200",
      },
      pending: {
        icon: Clock,
        label: "PENDING",
        class: "bg-amber-100 text-amber-700 border-amber-200",
      },
      rejected: {
        icon: XCircle,
        label: "REJECTED",
        class: "bg-red-100 text-red-700 border-red-200",
      },
      paused: {
        icon: Pause,
        label: "PAUSED",
        class: "bg-orange-100 text-orange-700 border-orange-200",
      },
      disabled: {
        icon: Ban,
        label: "DISABLED",
        class: "bg-red-100 text-red-700 border-red-200",
      },
      archived: {
        icon: Archive,
        label: "ARCHIVED",
        class: "bg-gray-100 text-gray-600 border-gray-200",
      },
      flagged: {
        icon: AlertTriangle,
        label: "FLAGGED",
        class: "bg-orange-100 text-orange-700 border-orange-200",
      },
      in_appeal: {
        icon: Clock,
        label: "IN APPEAL",
        class: "bg-amber-100 text-amber-700 border-amber-200",
      },
      pending_deletion: {
        icon: Trash2,
        label: "PENDING DELETION",
        class: "bg-red-100 text-red-700 border-red-200",
      },
      deleted: {
        icon: Trash2,
        label: "DELETED",
        class: "bg-gray-100 text-gray-600 border-gray-200",
      },
      locked: {
        icon: Lock,
        label: "LOCKED",
        class: "bg-gray-100 text-gray-700 border-gray-200",
      },
      limit_exceeded: {
        icon: AlertTriangle,
        label: "LIMIT EXCEEDED",
        class: "bg-red-100 text-red-700 border-red-200",
      },
      draft: {
        icon: Edit,
        label: "DRAFT",
        class: "bg-gray-100 text-gray-700 border-gray-200",
      },
    };

    const result = configs[normalizedStatus] || {
      icon: Edit,
      label: normalizedStatus.replace(/_/g, " ").toUpperCase(),
      class: "bg-gray-100 text-gray-700 border-gray-200",
    };
    return result;
  };

  const getQualityBadge = (quality) => {
    if (!quality) return {
      label: "UNKNOWN",
      class: "bg-gray-100 text-gray-700 border-gray-200",
    };

    const normalizedQuality = String(quality).toUpperCase().trim();

    const configs = {
      GREEN: {
        label: "GREEN",
        class: "bg-emerald-100 text-emerald-700 border-emerald-200",
      },
      YELLOW: {
        label: "YELLOW",
        class: "bg-amber-100 text-amber-700 border-amber-200",
      },
      RED: {
        label: "RED",
        class: "bg-red-100 text-red-700 border-red-200",
      },
      UNKNOWN: {
        label: "UNKNOWN",
        class: "bg-gray-100 text-gray-700 border-gray-200",
      },
    };

    return configs[normalizedQuality] || configs.UNKNOWN;
  };

  const statusChip = (status) => {
    const n = String(status || "").toLowerCase();
    if (n === "approved") return "g";
    if (["pending", "in_appeal", "paused", "flagged"].includes(n)) return "a";
    if (["rejected", "disabled", "pending_deletion", "limit_exceeded"].includes(n)) return "r";
    return "n";
  };

  const qualityChip = (quality) => {
    const n = String(quality || "").toUpperCase();
    if (n === "GREEN") return "g";
    if (n === "YELLOW") return "a";
    if (n === "RED") return "r";
    return "n";
  };

  // Category / language options from server (only used values)
  const categoryFilterOptions = filterOptions.categories.map((cat) => ({
    id: cat,
    label: getCategoryLabel(cat),
  }));

  const languageFilterOptions = filterOptions.languages.map((code) => ({
    id: code,
    label: getLanguageLabel(code),
  }));

  const statusFilterOptions = TEMPLATE_STATUS_FILTER_OPTIONS;

  // Calculate pagination info
  const totalPages = Math.ceil(totalTemplates / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalTemplates);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="tpl-page">
      <PhoneSelector section="templates" />
      <div className="page-head">
        <div>
          <h2>Templates</h2>
          <p>Manage your WhatsApp message templates</p>
        </div>
        <div className="grow" />
        <button
          type="button"
          onClick={() => setShowCompare(true)}
          disabled={!selectedPhoneId}
          className="ping-btn ping-btn-ghost ping-btn-sm"
          title="Compare two templates"
        >
          <GitCompare size={16} />
          Compare
        </button>
        <button
          type="button"
          onClick={handleSyncTemplates}
          disabled={isSyncing}
          className="ping-btn ping-btn-ghost ping-btn-sm"
          title="Sync templates from Meta"
        >
          {isSyncing ? <Loader size={16} className="animate-spin" /> : <CloudDownload size={16} />}
          {isSyncing ? "Syncing…" : "Sync from Meta"}
        </button>
        <button
          type="button"
          onClick={() => navigate("/templates/create")}
          className="ping-btn ping-btn-primary ping-btn-sm"
        >
          <Plus size={16} />
          New template
        </button>
      </div>

      <div className="tpl-kpis">
        <div className="tpl-kpi hi">
          <div>
            <div className="k">Total templates</div>
            <div className="v">{totalTemplates}</div>
          </div>
          <div className="ico"><MessageSquare size={14} /></div>
        </div>
        <div className="tpl-kpi">
          <div>
            <div className="k">Approved</div>
            <div className="v">{counts.approved || 0}</div>
          </div>
          <div className="ico"><CheckCircle size={14} /></div>
        </div>
        <div className="tpl-kpi">
          <div>
            <div className="k">Pending</div>
            <div className="v">{counts.pending || 0}</div>
          </div>
          <div className="ico"><Clock size={14} /></div>
        </div>
      </div>

        {error && (
          <div className="ping-banner red tpl-banner">
            <AlertCircle size={16} />
            <div>
              <b>{error}</b>
            </div>
            <button type="button" onClick={() => setError("")} aria-label="Dismiss">
              <XCircle size={16} />
            </button>
          </div>
        )}

        {syncSuccess && (
          <div className="ping-banner tpl-banner ok">
            <CheckCircle size={16} />
            <div>
              <b>{syncSuccess.message}</b>
              <p>{syncSuccess.details} • Completed in {(syncSuccess.duration / 1000).toFixed(1)}s</p>
            </div>
            <button type="button" onClick={() => setSyncSuccess(null)} aria-label="Dismiss">
              <XCircle size={16} />
            </button>
          </div>
        )}

        <div className="tpl-toolbar">
          <div className="tpl-search">
            <Search size={15} />
            <input
              type="text"
              placeholder="Search templates…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <TemplateCheckboxFilter
            label="Category"
            options={categoryFilterOptions}
            selectedIds={selectedCategories}
            onApply={setSelectedCategories}
          />
          <TemplateCheckboxFilter
            label="Language"
            options={languageFilterOptions}
            selectedIds={selectedLanguages}
            onApply={setSelectedLanguages}
          />
          <TemplateCheckboxFilter
            label="Status"
            options={statusFilterOptions}
            selectedIds={selectedStatusFilters}
            onApply={setSelectedStatusFilters}
          />
          <TemplateTimeRangeFilter
            value={daysBack}
            options={TEMPLATE_TIME_RANGE_OPTIONS}
            onChange={setDaysBack}
          />
        </div>

        {isLoading && (
          <div className="tpl-empty">
            <Loader size={28} className="animate-spin" />
            <p>Loading templates…</p>
          </div>
        )}

        {!isLoading && templates.length === 0 && (
          <div className="tpl-empty">
            <div className="tpl-empty-ico">
              <MessageSquare size={22} />
            </div>
            <h3>No templates yet</h3>
            <p>Create your first message template to start broadcasting.</p>
            <button type="button" onClick={() => navigate("/templates/create")} className="ping-btn ping-btn-primary ping-btn-sm">
              <Plus size={16} />
              Create template
            </button>
          </div>
        )}

        {!isLoading && templates.length > 0 && (
          <>
            <div className="tpl-grid">
              {templates.map((template) => {
                const statusConfig = getStatusBadge(template.status);
                const StatusIcon = statusConfig.icon;
                const qualityConfig = getQualityBadge(template.quality_score);
                const TypeIcon = getTypeIcon(template.template_type);
                const preview = String(template.body || "").replace(/\s+/g, " ").trim();

                return (
                  <article key={template.id} className="tpl-card">
                    <div className="tpl-card-top">
                      <div className="tpl-card-ico">
                        <TypeIcon size={14} />
                      </div>
                      <h3>{template.name}</h3>
                      <span className={`ping-chip ${statusChip(template.status)}`}>
                        <StatusIcon size={11} />
                        {statusConfig.label}
                      </span>
                    </div>
                    <div className="tpl-card-sub">
                      {getCategoryLabel(template.category) || String(template.category || "—").toUpperCase()}
                      {" · "}
                      {getLanguageLabel(template.language) || template.language || "N/A"}
                      {qualityConfig.label !== "UNKNOWN" && (
                        <>
                          {" · "}
                          <span className={`ping-chip ${qualityChip(template.quality_score)}`}>
                            {qualityConfig.label}
                          </span>
                        </>
                      )}
                    </div>
                    <p className="tpl-card-body">
                      {preview || "No body text"}
                    </p>
                    {template.status?.toLowerCase() === "rejected" && template.rejected_reason && (
                      <div className="tpl-reject">
                        <AlertCircle size={11} />
                        {template.rejected_reason}
                      </div>
                    )}
                    <div className="tpl-card-foot">
                      <span className="tpl-date">
                        {template.created_at
                          ? new Date(template.created_at).toLocaleDateString()
                          : ""}
                      </span>
                      <div className="tpl-actions">
                        {template.template_id &&
                          (template.status?.toLowerCase() === "pending" ||
                            template.status?.toLowerCase() === "rejected") && (
                            <button
                              type="button"
                              onClick={() => handleRefreshStatus(template)}
                              disabled={refreshingId === template.id}
                              className="camp-ico"
                              data-tip="Refresh status"
                              aria-label="Refresh status"
                            >
                              <RefreshCw size={14} className={refreshingId === template.id ? "animate-spin" : ""} />
                            </button>
                          )}
                        <button
                          type="button"
                          onClick={() => handleDuplicate(template)}
                          disabled={duplicatingId === template.id}
                          className="camp-ico"
                          data-tip="Duplicate"
                          aria-label="Duplicate"
                        >
                          {duplicatingId === template.id ? <Loader size={14} className="animate-spin" /> : <Copy size={14} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleViewEdit(template)}
                          className="camp-ico"
                          data-tip="View / edit"
                          aria-label="View or edit"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(template.id)}
                          className="camp-ico danger"
                          data-tip="Delete"
                          aria-label="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="camp-pager">
                <div className="camp-pager-info">
                  Showing <b>{startIndex}</b> to <b>{endIndex}</b> of <b>{totalTemplates}</b> templates
                </div>
                <div className="camp-pager-btns">
                  <label className="tpl-per">
                    Per page
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="ping-btn ping-btn-ghost ping-btn-sm"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    return (
                      <button
                        key={pageNum}
                        type="button"
                        onClick={() => handlePageChange(pageNum)}
                        className={`ping-btn ping-btn-sm ${currentPage === pageNum ? "ping-btn-primary" : "ping-btn-ghost"}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ping-btn ping-btn-ghost ping-btn-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

      {/* View Modal */}
      {selectedTemplate && (
        <ViewEditTemplateModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedTemplate(null);
          }}
          template={selectedTemplate}
        />
      )}
      <CompareTemplatesModal
        isOpen={showCompare}
        onClose={() => setShowCompare(false)}
        phoneId={selectedPhoneId}
        templates={templates}
      />
    </div>
  );
};

export default Templates;
